"use client"

import clsx from "clsx"
import * as React from "react"
import { BaseComponentWithChildrenProps } from "../../utils"

/**
 * 폼 필드 래퍼
 *
 * @description
 * 입력 컴포넌트(TTextInput, TSelect 등)를 감싸서 label과 helper 텍스트를 추가합니다.
 * required일 경우 라벨에 * 표시가 자동으로 붙습니다.
 *
 * @example
 * ```tsx
 * <TField label="이름" required>
 *   <TTextInput value={name} onChange={setName} />
 * </TField>
 *
 * <TField label="설명" helper="최대 200자">
 *   <TTextarea defaultValue={desc} onChange={setDesc} />
 * </TField>
 * ```
 */
interface TFieldProps extends Omit<BaseComponentWithChildrenProps, "id" | "dataAttributes"> {
	/** 필드 라벨 */
	label?: React.ReactNode
	/**
	 * 라벨 크기
	 * @default "medium"
	 * @ai-style "small" → text-xs, 연한 색상
	 * @ai-style "medium" → text-sm, 연한 색상
	 * @ai-style "large" → text-base, 기본 색상
	 */
	size?: "small" | "medium" | "large"
	/** 필수 필드 표시 (라벨 뒤에 * 추가) */
	required?: boolean
	/** 하단 도움말 텍스트 (THelper 컴포넌트 전달 권장) */
	helper?: React.ReactNode
}

export const TField = React.memo((props: TFieldProps) => {
	const { label, size = "medium", required, helper, className, children } = props

	return (
		<div className={clsx("TField", className)}>
			{label && <div className={clsx("label", size, { required })}>{label}</div>}
			{children}
			{helper && helper}
		</div>
	)
})

TField.displayName = "TField"
